/**
 * Global error handler aligned with the GetPoints API error standard.
 * Every frontend error (API or unexpected) should go through this helper.
 */

export interface ApiError {
  timestamp: string;
  status: number;
  error: string;
  message: string;
  path: string;
  traceId: string;
  details?: string[];
}

export class ApiException extends Error {
  public readonly status: number;
  public readonly error: string;
  public readonly path: string;
  public readonly traceId: string;
  public readonly details?: string[];
  public readonly timestamp: string;

  constructor(err: ApiError) {
    super(err.message);
    this.name = "ApiException";
    this.status = err.status;
    this.error = err.error;
    this.path = err.path;
    this.traceId = err.traceId;
    this.details = err.details;
    this.timestamp = err.timestamp;
  }
}

/**
 * Check if a value is an API exception.
 */
export function isApiException(err: unknown): err is ApiException {
  return err instanceof ApiException;
}

/**
 * Extract a user-friendly message from any error.
 * Prefers the API `message` field and never leaks stack traces.
 */
export function getErrorMessage(err: unknown): string {
  if (isApiException(err)) {
    return err.message;
  }

  if (err instanceof Error) {
    // Translate common network error messages
    if (err.message === 'Failed to fetch' || err.message === 'NetworkError when attempting to fetch resource.') {
      return 'Servidor indisponível no momento. Verifique sua conexão e tente novamente.';
    }
    if (err.message === 'AbortError' || err.message === 'The operation was aborted') {
      return 'A requisição demorou demais. Tente novamente.';
    }
    return err.message;
  }

  if (typeof err === "string") {
    return err;
  }

  return "Ocorreu um erro inesperado. Tente novamente.";
}

/**
 * Extract technical details (traceId + details) for logging/support.
 */
export function getErrorDetails(err: unknown): {
  traceId?: string;
  details?: string[];
  status?: number;
} {
  if (isApiException(err)) {
    return {
      traceId: err.traceId,
      details: err.details,
      status: err.status,
    };
  }

  // Network errors
  if (err instanceof Error && (err.message === 'Failed to fetch' || err.message === 'NetworkError when attempting to fetch resource.')) {
    return {
      status: 0,
      details: ['Erro de conexão. Verifique se o servidor está online ou se há problemas de rede.'],
    };
  }

  return {};
}

/**
 * Build a toast-ready payload from any API or unexpected error.
 */
export function buildErrorPayload(err: unknown): {
  message: string;
  traceId?: string;
  details?: string[];
} {
  return {
    message: getErrorMessage(err),
    ...getErrorDetails(err),
  };
}